import { useState, useMemo } from 'react';
import { Form } from 'antd';

import { useProducts, useAddProducts } from '../../hooks';
import { formatNumber, notify, removeObjectProperties, searchFn } from '../../utils';
import {
  Card,
  EmptyState,
  FormActions,
  FormSkeleton,
  PageHeader,
  QtyRow,
  SearchInput,
  StockBadge,
} from '../../components';
import { ROUTE_URLS } from '../../routes';
import { useI18n } from '../../i18n';

const sumPositive = values =>
  Object.values(values ?? {}).reduce(
    (acc, value) => {
      const n = Number(value) || 0;
      return n > 0 ? { filled: acc.filled + 1, units: acc.units + n } : acc;
    },
    { filled: 0, units: 0 },
  );

export const AddProduct = () => {
  const { t } = useI18n();
  const [searchText, setSearchText] = useState('');
  const [summary, setSummary] = useState({ filled: 0, units: 0 });
  const { data: products, isLoading: isDataLoading } = useProducts();
  const { mutateAsync: addProducts, isLoading } = useAddProducts();

  const defaultValues = useMemo(() => {
    const values = {};
    products?.forEach(product => {
      values[product.id] = product.sell_price ?? 0;
    });
    return values;
  }, [products]);

  const visibleIds = useMemo(() => {
    if (!searchText) return null;
    return new Set(searchFn(products, searchText).map(product => product.id));
  }, [products, searchText]);

  const isValid = data => {
    for (const key in data) {
      if (+data[key] < 0) {
        return false;
      }
    }
    return true;
  };

  const onFinish = values => {
    const readyValues = {
      data: {
        ...removeObjectProperties(values, ['productName']),
      },
    };
    if (!isValid(readyValues.data)) {
      return notify('error', t('products.negativeError'));
    }
    addProducts(readyValues);
  };

  const hasMatches = !visibleIds || visibleIds.size > 0;

  return (
    <div className='wm-form-page'>
      <PageHeader
        backTo={ROUTE_URLS.PRODUCTS}
        backLabel={t('nav.products')}
        title={t('products.addStockTitle')}
        subtitle={t('products.addStockSubtitle')}
      />
      {isDataLoading ? (
        <Card>
          <FormSkeleton fields={6} />
        </Card>
      ) : (
        <Form
          name='fields'
          layout='vertical'
          onFinish={onFinish}
          onFinishFailed={() => setSearchText('')}
          onValuesChange={(_, all) => setSummary(sumPositive(all))}
          initialValues={{
            ...defaultValues,
          }}
        >
          <Card
            title={t('createReport.products')}
            subtitle={products ? t('products.count', { count: products.length }) : undefined}
            extra={
              <SearchInput value={searchText} onChange={setSearchText} placeholder={t('products.searchPlaceholder')} />
            }
          >
            {!hasMatches && <EmptyState variant='search' compact />}
            {!products?.length && <EmptyState compact />}
            <div className='wm-qty-list'>
              {products?.map(product => (
                <QtyRow
                  key={product.id}
                  hidden={visibleIds && !visibleIds.has(product.id)}
                  name={product.id}
                  title={product.name}
                  meta={
                    <>
                      <span>{t('products.currentStock', { count: formatNumber(product.quantity) })}</span>
                      <StockBadge quantity={product.quantity} />
                    </>
                  }
                  placeholder={t('products.addQuantity')}
                  requiredMessage={t('validation.required', { field: t('products.addQuantity') })}
                />
              ))}
            </div>
          </Card>
          <FormActions
            cancelTo={ROUTE_URLS.PRODUCTS}
            submitting={isLoading}
            info={
              summary.filled > 0 && (
                <>
                  <b>{t('createReport.filled', { count: summary.filled })}</b>
                  <span>{t('createReport.totalUnits', { count: formatNumber(summary.units) })}</span>
                </>
              )
            }
          />
        </Form>
      )}
    </div>
  );
};